// Tiny DOM helpers shared by every view. No framework: elements are built by hand.

export type Child = Node | string | null | undefined | false;

interface ElementProps {
  class?: string;
  text?: string;
  html?: string;
  title?: string;
  type?: string;
  value?: string;
  placeholder?: string;
  href?: string;
  src?: string;
  alt?: string;
  id?: string;
  rows?: number;
  cols?: number;
  dataset?: Record<string, string>;
  attrs?: Record<string, string>;
  on?: Partial<Record<string, (event: Event) => void>>;
}

/** Creates an element; `props` are applied, `children` appended. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: ElementProps = {},
  children: Child[] = [],
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (props.class) el.className = props.class;
  if (props.text !== undefined) el.textContent = props.text;
  if (props.html !== undefined) el.innerHTML = props.html;
  if (props.title !== undefined) el.title = props.title;
  if (props.id) el.id = props.id;
  if (props.dataset) for (const [key, value] of Object.entries(props.dataset)) el.dataset[key] = value;
  if (props.attrs) for (const [key, value] of Object.entries(props.attrs)) el.setAttribute(key, value);
  if (
    (tag === 'input' || tag === 'textarea' || tag === 'select' || tag === 'button') &&
    (props.type || props.value !== undefined || props.placeholder)
  ) {
    const control = el as unknown as HTMLInputElement;
    if (props.type) control.type = props.type;
    if (props.value !== undefined) control.value = props.value;
    if (props.placeholder) control.placeholder = props.placeholder;
  }
  if ((tag === 'textarea' || tag === 'input') && (props.rows !== undefined || props.cols !== undefined)) {
    const control = el as unknown as HTMLTextAreaElement;
    if (props.rows !== undefined) control.rows = props.rows;
    if (props.cols !== undefined) control.cols = props.cols;
  }
  if (tag === 'a' && props.href) (el as HTMLAnchorElement).href = props.href;
  if (tag === 'img' && props.src) (el as HTMLImageElement).src = props.src;
  if (tag === 'img' && props.alt) (el as HTMLImageElement).alt = props.alt;
  if (props.on) {
    for (const [name, handler] of Object.entries(props.on)) {
      if (handler) el.addEventListener(name, handler);
    }
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    el.append(child);
  }
  return el;
}

export function button(label: string, className: string, onClick: () => void, title?: string): HTMLButtonElement {
  return h('button', {
    class: className,
    text: label,
    type: 'button',
    ...(title ? { title } : {}),
    on: { click: () => onClick() },
  });
}

export function clear(node: Element): void {
  node.replaceChildren();
}

/** Escapes vault text before it is injected into an HTML template string. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Search snippets arrive pre-marked with `<mark>`; escape everything else. */
export function snippetHtml(snippet: string): string {
  return snippet
    .split(/(<mark>|<\/mark>)/)
    .map((part) => (part === '<mark>' || part === '</mark>' ? part : escapeHtml(part)))
    .join('');
}

export function basename(path: string): string {
  const i = path.lastIndexOf('/');
  return i === -1 ? path : path.slice(i + 1);
}

export function dirname(path: string): string {
  const i = path.lastIndexOf('/');
  return i === -1 ? '' : path.slice(0, i);
}

/** `notes/a.md` → `notes/a` (used for wikilink targets and note titles). */
export function stripMarkdownExt(path: string): string {
  return path.replace(/\.(md|markdown)$/i, '');
}

export function isMarkdown(path: string): boolean {
  return /\.(md|markdown)$/i.test(path);
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatTime(ts: number | null | undefined): string {
  if (!ts) return '—';
  const date = new Date(ts);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
}

/** Debounces `fn`: used for autosave, search-as-you-type and tree refreshes. */
export function debounce(fn: () => void, ms: number): () => void {
  let timer: number | undefined;
  return () => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = undefined;
      fn();
    }, ms);
  };
}

/** Joins vault paths without producing empty or duplicated slashes. */
export function joinPath(...parts: string[]): string {
  return parts
    .filter((part) => part.length > 0)
    .map((part, index) => (index === 0 ? part.replace(/\/+$/, '') : part.replace(/^\/+|\/+$/g, '')))
    .join('/');
}

/** Resolves a relative markdown link against the directory of `fromPath`. */
export function resolveRelative(fromPath: string, target: string): string {
  const decoded = decodeURIComponent(target.split('#')[0]!.split('?')[0]!);
  if (decoded.startsWith('/')) return decoded.replace(/^\/+/, '');
  const base = dirname(fromPath).split('/').filter((part) => part.length > 0);
  for (const part of decoded.split('/')) {
    if (part === '' || part === '.') continue;
    if (part === '..') base.pop();
    else base.push(part);
  }
  return base.join('/');
}
